import React from 'react';
import styled from 'styled-components';
import Figure from '../Figure';
import Flex from '../Flex';
import Icon from '../Icon';
import Typo from '../Typo';
import SimpleButton from '../molecules/Button/SimpleButton';
import theme from '../theme';
import { toRgba } from '../theme/colors';
import { priceFormater } from '../../helpers/formatNumbers';
import { media } from '../../utils/media';

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.NEU_0};
  box-shadow: ${(props) => props.theme.shadows.BOX_SHADOW_SMALL};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  width: 100%;
  max-width: 375px;

  ${media.lessThan('tablet')} {
    max-width: 100%;
  }
`;

const ImageSection = styled.div`
  position: relative;
  width: 100%;
`;

const BundleTag = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background-color: ${({ $bgColor }) => $bgColor};
  padding: 6px 8px;
  border-radius: 8px;
  z-index: 1;
`;

const MatchBadge = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background-color: ${(props) => props.theme.colors.NEU_0};
  padding: 6px 12px 6px 8px;
  border-radius: 16px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 3px;
`;

const ImagePlaceholder = styled.div`
  background-color: ${(props) => props.theme.colors.NEU_200};
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 12px 24px 28px;
  flex: 1;
`;

const Description = styled(Typo.SmallText)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  margin-top: auto;
`;

const CourseGridCard = ({ courseInfo, onBuy, onCardClick, hideVAT, matchPercentage, buyLabel }) => {
  const { coverImage, coverUrl, name, description, price, currency } = courseInfo;
  const imageUrl = !coverImage ? coverUrl : Array.isArray(coverImage) ? coverImage[0]?.url : coverImage?.url;

  const isPackage = courseInfo?.type === 2;
  const packageSize = courseInfo?.courses?.length;
  const ownerName = courseInfo?.ownerName || courseInfo?.owner?.name;

  const handleBuy = (e) => {
    e.stopPropagation();
    if (onBuy) onBuy(courseInfo);
  };

  const handleCardClick = () => {
    if (onCardClick) onCardClick(courseInfo);
  };

  return (
    <CardWrapper onClick={handleCardClick}>
      <ImageSection>
        {isPackage && packageSize && (
          <BundleTag $bgColor={toRgba(theme.colors.NEU_999, 0.65)}>
            <Typo.Info $themeColor={theme.colors.NEU_999}>{`${packageSize}-in-1`}</Typo.Info>
          </BundleTag>
        )}
        {matchPercentage != null && (
          <MatchBadge>
            <Icon.Bolt size='x-small' color={theme.colors.SECONDARY} />
            <Typo.Info>{`Match ${matchPercentage}%`}</Typo.Info>
          </MatchBadge>
        )}
        <Figure.Rectangle>{!imageUrl ? <ImagePlaceholder /> : <img src={imageUrl} alt={name} loading='lazy' />}</Figure.Rectangle>
      </ImageSection>

      <InfoSection>
        <Flex justify='space-between' width='100%'>
          {ownerName && <Typo.Info $caps>{ownerName}</Typo.Info>}
          <Typo.TextTitle>{priceFormater(price, hideVAT, currency)}</Typo.TextTitle>
        </Flex>

        <TitleGroup>
          <Typo.TextTitle>{name}</Typo.TextTitle>
          {description && <Description>{description}</Description>}
        </TitleGroup>

        {onBuy && (
          <ButtonWrapper>
            <SimpleButton variant='secondary' fullWidth onClick={handleBuy}>
              {buyLabel || 'Buy now'}
            </SimpleButton>
          </ButtonWrapper>
        )}
      </InfoSection>
    </CardWrapper>
  );
};

export default CourseGridCard;
